import { PartialId } from '@attain-apps/string-builder'
import { TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../common'

import { Id } from './id'
import type { Identifiable } from './identifiable'
import { Term } from './term'
import type { Type } from './type'

type ModelBase<TId extends Id = Id> = common.Identified<TId> &
  common.Named &
  common.PotentiallyDescribed
export interface Model<TId extends Id = Id> extends ModelBase<TId> {
  terms?: readonly Term[]
  note?: string
}
export namespace Model {
  export function getTypeChecker<TId extends Id>(
    idTypeChecker: TypeChecker<TId>,
  ): TypeChecker<Model<TId>> {
    return TypeChecker.Factory.intersection(
      common.Identified.getTypeChecker(idTypeChecker),
      common.Named.isNamed,
      common.PotentiallyDescribed.isPotentiallyDescribed,
      TypeChecker.Factory.object<Difference<Model, ModelBase>>({
        note: TypeChecker.Factory.optional(TypeChecker.isString),
        terms: TypeChecker.Factory.optional(
          TypeChecker.Factory.list(Term.isTerm),
        ),
      }),
    ).usingAlias(() => `Model<${idTypeChecker.typeDescription}>`)
  }
  export const isModel = TypeChecker.Factory.lazy(() =>
    getTypeChecker(Id.isId),
  ).usingAlias('Model')

  export function getLooseTypeChecker<TType extends Type>(
    type: TType,
  ): TypeChecker<common.Identified<Id<TType>>> {
    return common.Identified.getTypeChecker(
      TypeChecker.Factory.custom(
        (val: any): val is Id<TType> =>
          TypeChecker.isString(val) && val.startsWith(type),
        { typeDescription: `Identified<Id<'${type}'>>` },
      ),
    )
  }

  /**
   * Indicates a given model is required by the system
   *
   * @see {@link PartialId.Standard}
   */
  export function isStandard(identifiable: Identifiable): boolean {
    const { partialId } = Id.Parts.from(identifiable)
    return PartialId.Standard.isStandard(partialId)
  }
  /**
   * Indicates a given model is NOT required by the system
   *
   * @see {@link PartialId.Custom}
   */
  export function isCustom(identifiable: Identifiable): boolean {
    return !isStandard(identifiable)
  }
}
